import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * Tema do painel. `system` é o padrão e significa "obedeça o sistema
 * operacional" — não é um terceiro visual, é a ausência de escolha explícita.
 *
 * Por que existe um provider e não só `prefers-color-scheme` no CSS: o dono
 * precisa poder escolher. Quem trabalha num Mac configurado no escuro nunca
 * via o tema claro do painel, mesmo preferindo.
 */
export type Theme = "light" | "dark" | "system";

/**
 * A chave está DUPLICADA no script inline do `index.html`, e tem que ser.
 * Aquele script roda antes de qualquer JS de módulo carregar — é ele que
 * evita o flash de tema errado na primeira pintura — então não tem como
 * importar esta constante. Mudar aqui exige mudar lá.
 */
export const THEME_STORAGE_KEY = "agendo-certo-theme";

const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

function storedTheme(): Theme {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return value === "light" || value === "dark" ? value : "system";
  } catch {
    // Safari em navegação privada pode lançar ao ler localStorage.
    return "system";
  }
}

function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia(DARK_MEDIA_QUERY).matches;
}

interface ThemeContextValue {
  /** O que a pessoa escolheu — pode ser `system`. */
  theme: Theme;
  /** O que está valendo na tela agora. É isto que a agenda precisa saber. */
  isDark: boolean;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(storedTheme);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  // O listener fica montado SEMPRE, e não só quando `theme === "system"`: a
  // pessoa pode trocar o tema do SO com o painel aberto, e ao voltar para
  // "Sistema" o valor precisa já estar certo — sem isso ela veria o tema
  // anterior até recarregar a página.
  useEffect(() => {
    const query = window.matchMedia(DARK_MEDIA_QUERY);
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const isDark = theme === "system" ? systemDark : theme === "dark";

  // A classe é a fonte da verdade do CSS inteiro: os tokens de `.dark` e
  // todas as utilitárias `dark:` do shadcn dependem dela.
  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
  }, [isDark]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      // "system" APAGA a chave em vez de gravar a string: assim quem nunca
      // escolheu e quem escolheu "Sistema" ficam no mesmo estado, e o script
      // do index.html só precisa testar "dark"/"light".
      if (next === "system") localStorage.removeItem(THEME_STORAGE_KEY);
      else localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Sem persistência a escolha vale só para esta aba — melhor que quebrar.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, isDark, setTheme }),
    [theme, isDark, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme precisa estar dentro de <ThemeProvider>");
  return context;
}
